/**
 * De facto eligibility evidence presenter (Migration Act s5CB).
 *
 * To submit a de facto partner visa (820/309 variant=defacto) the registered
 * migration agent (Eli, MARN 1465541) must be satisfied that ONE of:
 *   - 12+ months cohabitation immediately preceding lodgement, OR
 *   - State-registered relationship under a prescribed Australian law, OR
 *   - Compelling and compassionate circumstances justifying the shorter period.
 *
 * The portal CAN'T decide this — it can't verify cohabitation dates, can't
 * verify a "relationship_certificate" doc came from a recognised registry,
 * and "≥30 chars of free text" is not a legal judgement. So this helper is
 * now a pure data presenter: it surfaces the evidence the matter currently
 * has, and Eli (or any admin) records the basis decision in
 * matters.de_facto_eligibility_basis. The DB trigger
 * fn_check_defacto_eligibility_basis() blocks lodgement transitions when
 * the basis is unrecorded.
 *
 * Per Migration Agents Code of Conduct reg. 8 (competent inquiry) the
 * decision is the agent's, not a script's. The basis note + audit-log
 * capture is the compliance paper trail.
 */

import { readNestedPath } from './schema-runtime/formStateAdapter';

// Re-exported for callers that only need the path reader (e.g. its own guard
// tests) without pulling in the full de-facto evidence surface.
export { readNestedPath };

export type DeFactoBasisPath =
  | 'cohabitation_12_months'
  | 'state_registered'
  | 'compelling_circumstances';

export type DeFactoRecordedBasis = {
  path: DeFactoBasisPath;
  note: string;
  decided_by_staff_id: string;
  decided_at: string;
};

export type DeFactoEligibilityInput = {
  variant: string | null;
  /** ISO date string for `applicant.relationship.date_de_facto_began`. */
  dateDeFactoBegan: string | null;
  /** True if a state-registered relationship certificate document exists on the matter. */
  hasRegisteredRelationshipCert: boolean;
  /** ID of the relationship_certificate document, when one exists. Used for deep-link previews. */
  registeredCertDocId?: string | null;
  /** Free text — `applicant.relationship.compelling_circumstances`. */
  compellingCircumstances: string | null;
  /** Count of approved documents in the relationship_evidence category. */
  cohabitationEvidenceDocsApproved?: number;
  /** Existing recorded basis on matters.de_facto_eligibility_basis, if any. */
  recordedBasis?: DeFactoRecordedBasis | null;
};

export type DeFactoEvidence = {
  /** True when matter.variant === 'defacto'. */
  applies: boolean;
  /** True when matters.de_facto_eligibility_basis IS NOT NULL. */
  basisRecorded: boolean;
  /** The recorded basis from the matter, or null. */
  recordedBasis: DeFactoRecordedBasis | null;
  /** Evidence currently on the matter for each of the three statutory paths. */
  evidence: {
    cohabitation: {
      startDate: string | null;
      monthsElapsed: number | null;
      evidenceDocsApproved: number;
    };
    registered: {
      certificateUploaded: boolean;
      certificateDocId: string | null;
    };
    compelling: {
      narrative: string | null;
      charCount: number;
    };
  };
};

/**
 * Pure data presenter — does NOT decide eligibility. Surfaces what the matter
 * currently has so Eli can record the basis decision.
 */
export function getDeFactoEvidence(
  input: DeFactoEligibilityInput,
  now: Date = new Date(),
): DeFactoEvidence {
  if (input.variant !== 'defacto') {
    return {
      applies: false,
      basisRecorded: false,
      recordedBasis: null,
      evidence: {
        cohabitation: { startDate: null, monthsElapsed: null, evidenceDocsApproved: 0 },
        registered: { certificateUploaded: false, certificateDocId: null },
        compelling: { narrative: null, charCount: 0 },
      },
    };
  }

  let monthsElapsed: number | null = null;
  if (input.dateDeFactoBegan) {
    const start = new Date(input.dateDeFactoBegan);
    if (!Number.isNaN(start.getTime())) {
      monthsElapsed = (now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30.4375);
    }
  }

  const narrative = input.compellingCircumstances?.trim() ?? null;

  return {
    applies: true,
    basisRecorded: !!input.recordedBasis,
    recordedBasis: input.recordedBasis ?? null,
    evidence: {
      cohabitation: {
        startDate: input.dateDeFactoBegan,
        monthsElapsed,
        evidenceDocsApproved: input.cohabitationEvidenceDocsApproved ?? 0,
      },
      registered: {
        certificateUploaded: input.hasRegisteredRelationshipCert,
        certificateDocId: input.registeredCertDocId ?? null,
      },
      compelling: {
        narrative,
        charCount: narrative ? narrative.length : 0,
      },
    },
  };
}

/**
 * Resolve the two applicant-owned de-facto intake fields
 * (applicant.relationship.date_de_facto_began / .compelling_circumstances)
 * from the applicant's own hydrated intake state, falling back to the
 * sponsor's hydrated intake state ONLY when the applicant's own value is
 * empty/undefined.
 *
 * WHY a fallback at all: these are applicant-owned canonical fields, but the
 * sponsor's 40SP form also renders them in its stage 1 (confirming shared
 * relationship facts). Early in a matter — before the applicant has reached
 * their own relationship section — only the sponsor-side copy exists. The
 * applicant's own value, once entered, always wins; it can never be
 * clobbered by a stale sponsor-side copy.
 */
export function resolveDeFactoIntakeFields(
  applicantState: Record<string, unknown>,
  sponsorState: Record<string, unknown> | null,
): { dateDeFactoBegan: string | null; compellingCircumstances: string | null } {
  const pick = (path: string): string | null => {
    const own = readNestedPath(applicantState, path);
    if (typeof own === 'string' && own.trim() !== '') return own;
    if (sponsorState) {
      const fromSponsor = readNestedPath(sponsorState, path);
      if (typeof fromSponsor === 'string' && fromSponsor.trim() !== '') return fromSponsor;
    }
    return null;
  };
  return {
    dateDeFactoBegan: pick('applicant.relationship.date_de_facto_began'),
    compellingCircumstances: pick('applicant.relationship.compelling_circumstances'),
  };
}

/** Human-readable label for each statutory path. */
export function deFactoPathLabel(path: DeFactoBasisPath): string {
  switch (path) {
    case 'cohabitation_12_months':
      return '12 months cohabitation';
    case 'state_registered':
      return 'State-registered relationship';
    case 'compelling_circumstances':
      return 'Compelling and compassionate circumstances';
  }
}
